import React, { useState, useCallback } from "react";
import { View, StyleSheet, Image, Pressable, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import Avatar from "@/components/Avatar";
import { useEntityEdit } from "@/contexts/EntityEditContext";

export interface AvatarSectionProps {
  entityId: string;
  entityType: "user" | "client";
  currentImageUrl?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  onImageSelect?: (file: File) => void;
  onImageRemove?: () => void;
}

export function AvatarSection({
  entityId,
  entityType,
  currentImageUrl,
  firstName,
  lastName,
  email,
  onImageSelect,
  onImageRemove,
}: AvatarSectionProps) {
  const { colors } = useTheme();
  const { editing, formData } = useEntityEdit();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImagePick = useCallback(() => {
    if (Platform.OS === "web") {
      // Web: use file input
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          // Validate file type
          if (!file.type.startsWith("image/")) {
            alert("Please select an image file");
            return;
          }

          // Validate file size (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            alert("Image must be less than 5MB");
            return;
          }

          // Create preview URL
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          setSelectedFile(file);

          // Notify parent
          onImageSelect?.(file);
        }
      };
      input.click();
    } else {
      // Mobile: would use image picker library
      console.log("Mobile image picker not implemented yet");
    }
  }, [onImageSelect]);

  const handleImageRemove = useCallback(() => {
    setPreviewUrl(null);
    setSelectedFile(null);
    onImageRemove?.();
  }, [onImageRemove]);

  // Determine which image to display
  const displayUrl = previewUrl || currentImageUrl;

  return (
    <View style={styles.container}>
      <View style={styles.imageContainer}>
        {entityType === "user" && !editing ? (
          // View mode for users: use Profile component for proper resolution
          <Avatar userId={entityId} firstName={firstName || ""} lastName={lastName || ""} size={120} />
        ) : displayUrl ? (
          // Edit mode or client with image: show image
          <Image source={{ uri: displayUrl }} style={[styles.image, { borderColor: colors.border }]} />
        ) : (
          // No image: show placeholder
          <View
            style={[styles.placeholder, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          >
            <MaterialCommunityIcons
              name={entityType === "user" ? "account" : "office-building"}
              size={60}
              color={colors.textSecondary}
            />
          </View>
        )}

        {editing && (
          <View style={styles.editOverlay}>
            <Pressable
              style={[styles.editButton, { backgroundColor: colors.primary }]}
              onPress={handleImagePick}
              accessibilityLabel={`Upload ${entityType === "user" ? "avatar" : "logo"}`}
            >
              <MaterialCommunityIcons name="camera" size={20} color="#ffffff" />
            </Pressable>

            {(previewUrl || currentImageUrl) && (
              <Pressable
                style={[styles.removeButton, { backgroundColor: colors.dangerText }]}
                onPress={handleImageRemove}
                accessibilityLabel="Remove image"
              >
                <MaterialCommunityIcons name="close" size={20} color="#ffffff" />
              </Pressable>
            )}
          </View>
        )}
      </View>

      <View style={styles.infoContainer}>
        <ThemedText style={styles.label}>{entityType === "user" ? "Profile Picture" : "Company Logo"}</ThemedText>
        {editing && (
          <ThemedText style={[styles.hint, { color: colors.textSecondary }]}>
            {selectedFile
              ? "New image selected. Click Save to upload."
              : "Click the camera icon to upload an image. Changes will be saved when you click Save."}
          </ThemedText>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    gap: 12,
    paddingVertical: 16,
  },
  imageContainer: {
    position: "relative",
    width: 120,
    height: 120,
  },
  image: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 2,
  },
  placeholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  editOverlay: {
    position: "absolute",
    bottom: 0,
    right: 0,
    flexDirection: "row",
    gap: 8,
  },
  editButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  removeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  infoContainer: {
    alignItems: "center",
    gap: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  hint: {
    fontSize: 12,
    textAlign: "center",
    maxWidth: 280,
  },
});
